import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { 
  Bell, AlertTriangle, Shield, Pill, Heart, FileText, 
  Brain, Activity, X, ChevronDown, ChevronUp 
} from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";

const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace("https://", "wss://").replace("http://", "ws://") || "ws://localhost:8001";

const NOTIFICATION_ICONS = {
  compliance_alert: Shield,
  medication_review: Pill,
  unsigned_document: FileText,
  patient_crisis: AlertTriangle,
  transition_update: Activity,
  bereavement_followup: Heart,
  ai_recommendation: Brain,
  system_alert: Bell
};

const PRIORITY_STYLES = {
  critical: "bg-red-500 border-red-600 text-white animate-pulse",
  high: "bg-orange-500 border-orange-600 text-white",
  medium: "bg-yellow-500 border-yellow-600 text-black",
  low: "bg-blue-500 border-blue-600 text-white"
};

export default function RealtimeNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  const connectWebSocket = useCallback(() => {
    const userId = localStorage.getItem("userId") || "anonymous";
    const wsUrl = `${WS_URL}/ws/notifications?user_id=${userId}`;
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        console.log("WebSocket connected for real-time notifications");
        setIsConnected(true);
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          // Handle different message types
          if (message.type === "connection") {
            console.log("Notification connection confirmed");
            return;
          }
          
          if (message.type === "heartbeat") {
            // Silent heartbeat acknowledgment
            return;
          }
          
          // Handle actual notifications
          if (message.type && message.data) {
            handleNotification(message);
          }
        } catch (e) {
          console.error("Error parsing WebSocket message:", e);
        }
      };
      
      wsRef.current.onclose = () => {
        console.log("WebSocket disconnected");
        setIsConnected(false);
        
        // Attempt reconnection after 5 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log("Attempting to reconnect WebSocket...");
          connectWebSocket();
        }, 5000);
      };
      
      wsRef.current.onerror = (error) => {
        console.error("WebSocket error:", error);
        setIsConnected(false);
      };
    } catch (error) {
      console.error("Failed to connect WebSocket:", error);
      setIsConnected(false);
    }
  }, []);

  const handleNotification = (message) => {
    const notification = {
      id: Date.now().toString(),
      type: message.type,
      priority: message.priority,
      timestamp: message.timestamp,
      data: message.data,
      read: false
    };
    
    setNotifications(prev => [notification, ...prev].slice(0, 50)); // Keep last 50
    setUnreadCount(prev => prev + 1);
    
    // Show toast for high priority notifications
    if (message.priority === "critical" || message.priority === "high") {
      const Icon = NOTIFICATION_ICONS[message.type] || Bell;
      
      toast(
        <div className="flex items-start gap-3">
          <Icon className={`w-5 h-5 ${message.priority === "critical" ? "text-red-500" : "text-orange-500"}`} />
          <div>
            <p className="font-medium">{message.data?.patient_name || message.data?.title || "Alert"}</p>
            <p className="text-sm text-gray-600">{message.data?.description || message.data?.message}</p>
          </div>
        </div>,
        {
          duration: message.priority === "critical" ? 10000 : 5000,
          position: "top-right"
        }
      );
    }
  };

  const markAsRead = (notificationId) => {
    setNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const clearAll = () => {
    setNotifications([]);
    setUnreadCount(0);
  };

  useEffect(() => {
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [connectWebSocket]);

  // Send ping every 25 seconds to keep connection alive
  useEffect(() => {
    const pingInterval = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send("ping");
      }
    }, 25000);
    
    return () => clearInterval(pingInterval);
  }, []);

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="relative">
      {/* Notification Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`relative p-2 rounded-lg transition-colors ${
          isOpen ? "bg-slate-700" : "hover:bg-slate-700/50"
        }`}
        data-testid="notification-bell"
      >
        <Bell className={`w-5 h-5 ${isConnected ? "text-slate-300" : "text-slate-500"}`} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs text-white flex items-center justify-center font-medium">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
        {!isConnected && (
          <span className="absolute bottom-0 right-0 w-2 h-2 bg-yellow-500 rounded-full" title="Reconnecting..." />
        )}
      </button>

      {/* Notification Panel */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-700 bg-slate-800/80">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-slate-400" />
              <span className="font-medium text-white">Notifications</span>
              {unreadCount > 0 && (
                <Badge className="bg-red-500">{unreadCount}</Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${isConnected ? "bg-green-500" : "bg-yellow-500"}`} />
              <button onClick={markAllAsRead} className="text-xs text-slate-400 hover:text-white">
                Mark all read
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-12 text-center text-slate-500">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No notifications yet</p>
                <p className="text-xs mt-1">Real-time alerts will appear here</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-700">
                {notifications.map(notification => {
                  const Icon = NOTIFICATION_ICONS[notification.type] || Bell;
                  const priorityStyle = PRIORITY_STYLES[notification.priority] || PRIORITY_STYLES.low;
                  
                  return (
                    <div
                      key={notification.id}
                      className={`p-4 hover:bg-slate-700/50 cursor-pointer transition-colors ${
                        !notification.read ? "bg-slate-700/30" : ""
                      }`}
                      onClick={() => markAsRead(notification.id)}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${priorityStyle.split(' ')[0]}`}>
                          <Icon className={`w-4 h-4 ${notification.priority === "medium" ? "text-black" : "text-white"}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-white text-sm truncate">
                              {notification.data?.patient_name || notification.data?.title || "Alert"}
                            </span>
                            <Badge className={`text-xs ${priorityStyle}`}>
                              {notification.priority}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-400 line-clamp-2">
                            {notification.data?.description || notification.data?.message || notification.data?.alert_type}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            {formatTime(notification.timestamp)}
                          </p>
                        </div>
                        {!notification.read && (
                          <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-3 border-t border-slate-700 bg-slate-800/80">
              <button
                onClick={clearAll}
                className="w-full py-2 text-sm text-slate-400 hover:text-white transition-colors"
              >
                Clear all notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
